import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BookOpen, Sparkles, Filter, ArrowUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { ENEMY_LORE, ENEMY_ORDER } from '../game/lore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier color chips
const TIER_COLORS = {
  Common:   { bg: '#8a8a8a22', text: '#a0a0a0', border: '#666' },
  Uncommon: { bg: '#4ADE8022', text: '#4ADE80', border: '#4ADE80' },
  Rare:     { bg: '#5AC8FA22', text: '#5AC8FA', border: '#5AC8FA' },
  Elite:    { bg: '#FFD70022', text: '#FFD700', border: '#FFD700' },
};

const TIER_ORDER = ['All', 'Common', 'Uncommon', 'Rare', 'Elite'];

/**
 * Bestiary / Enemy Codex
 *
 * Grid of every enemy type. Each card shows the name, tier chip, description,
 * and the player's cumulative kill count against that type (from
 * `/api/player/bestiary/{wallet_or_guest}`). Types the player has never killed
 * show a "Not yet encountered" state instead of the count.
 */
const Bestiary = ({ walletAddress, onBack }) => {
  const [bestiary, setBestiary] = useState({ kills_by_type: {}, total_kills: 0, unique_enemies: 0 });
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('All');
  const [sortByKills, setSortByKills] = useState(false);

  useEffect(() => {
    let ident = walletAddress;
    if (!ident) {
      ident = sessionStorage.getItem('metal_lego_guest_id');
    }
    if (!ident) { setLoading(false); return; }
    axios.get(`${API}/player/bestiary/${ident}`)
      .then(res => setBestiary(res.data))
      .catch(err => console.error('[Bestiary] fetch failed:', err))
      .finally(() => setLoading(false));
  }, [walletAddress]);

  const killsByType = bestiary.kills_by_type || {};

  // Apply tier filter + optional kill-count sort. Default sort (sortByKills=false)
  // is the canonical ENEMY_ORDER — grouped by tier/theme for browsing.
  const visibleEnemyTypes = useMemo(() => {
    let list = ENEMY_ORDER.filter(t => {
      if (tierFilter === 'All') return true;
      return ENEMY_LORE[t]?.tier === tierFilter;
    });
    if (sortByKills) {
      list = [...list].sort((a, b) => (killsByType[b] || 0) - (killsByType[a] || 0));
    }
    return list;
  }, [tierFilter, sortByKills, killsByType]);

  return (
    <div data-testid="bestiary-screen" className="min-h-screen bg-[#0a0a0a] text-white p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-game text-4xl sm:text-5xl text-[#4ADE80] flex items-center gap-3">
              <BookOpen className="w-10 h-10" /> BESTIARY
            </h1>
            <p className="text-sm text-gray-400 mt-2">
              {bestiary.unique_enemies || 0} of {ENEMY_ORDER.length} enemy types encountered · {bestiary.total_kills || 0} total kills
            </p>
          </div>
          <Button
            data-testid="bestiary-back-btn"
            onClick={onBack}
            variant="outline"
            className="border-[#4ADE80] text-[#4ADE80] hover:bg-[#4ADE80]/10"
          >
            ← BACK
          </Button>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-16">Loading bestiary…</p>
        ) : (
          <>
            {/* Tier filter chips + sort toggle */}
            <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="bestiary-controls">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-gray-500" />
                {TIER_ORDER.map((tier) => {
                  const active = tierFilter === tier;
                  const count = tier === 'All'
                    ? ENEMY_ORDER.length
                    : ENEMY_ORDER.filter(t => ENEMY_LORE[t]?.tier === tier).length;
                  const chipColor = TIER_COLORS[tier] || { bg: '#4ADE8022', text: '#4ADE80', border: '#4ADE80' };
                  return (
                    <button
                      key={tier}
                      onClick={() => setTierFilter(tier)}
                      data-testid={`tier-chip-${tier.toLowerCase()}`}
                      className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                        active ? 'ring-2 ring-white/40' : 'opacity-70 hover:opacity-100'
                      }`}
                      style={{
                        background: active ? chipColor.text + '33' : chipColor.bg,
                        color: chipColor.text,
                        border: `1px solid ${chipColor.border}`,
                      }}
                    >
                      {tier} <span className="opacity-60 ml-1">({count})</span>
                    </button>
                  );
                })}
              </div>
              <div className="ml-auto">
                <button
                  onClick={() => setSortByKills(!sortByKills)}
                  data-testid="sort-by-kills-btn"
                  className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 ${
                    sortByKills
                      ? 'bg-[#4ADE80] text-black border border-[#4ADE80]'
                      : 'bg-transparent text-[#4ADE80] border border-[#4ADE80]/50 hover:border-[#4ADE80]'
                  }`}
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  {sortByKills ? 'Sorted by Kills' : 'Sort by Kills'}
                </button>
              </div>
            </div>

            {visibleEnemyTypes.length === 0 ? (
              <p className="text-center text-gray-500 py-16">No enemies match this filter.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleEnemyTypes.map((enemyType) => {
              const enemy = ENEMY_LORE[enemyType];
              const killCount = killsByType[enemyType] || 0;
              const encountered = killCount > 0;
              const tierColor = TIER_COLORS[enemy.tier] || TIER_COLORS.Common;
              return (
                <div
                  key={enemyType}
                  data-testid={`enemy-card-${enemyType}`}
                  className={`relative rounded-lg border-2 p-5 transition-all ${
                    encountered
                      ? 'border-[#333] bg-[#151515] hover:border-[#4ADE80] hover:shadow-[0_0_15px_rgba(74,222,128,0.15)]'
                      : 'border-gray-800 bg-[#0f0f0f] opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-game text-base text-white flex items-center gap-2">
                      {enemy.name}
                      {enemy.isNew && (
                        <span className="text-[10px] font-bold text-black bg-[#FFD700] px-1.5 py-0.5 rounded flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> NEW
                        </span>
                      )}
                    </h3>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap"
                      style={{
                        background: tierColor.bg,
                        color: tierColor.text,
                        border: `1px solid ${tierColor.border}`,
                      }}
                    >
                      {enemy.tier}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed mb-3 min-h-[3.5em]">
                    {enemy.description}
                  </p>
                  <div className="pt-2 border-t border-[#222] flex items-center justify-between">
                    <span className="text-xs text-gray-500">Kills</span>
                    <span
                      data-testid={`enemy-kills-${enemyType}`}
                      className={`font-game text-lg ${encountered ? 'text-[#4ADE80]' : 'text-gray-600'}`}
                    >
                      {encountered ? killCount.toLocaleString() : '—'}
                    </span>
                  </div>
                </div>
              );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Bestiary;
